import * as Color from 'color';

/**
 * Color definitions
 */
export const SOTICOLORS = {
  blueGrey: {
    5: '#F4F7FB',
    10: '#EAEFF6',
    20: '#CCD7E1',
    30: '#AEBCCA',
    40: '#8FA1B3',
    50: '#788CA0',
    60: '#60798F',
    70: '#546A7E',
    80: '#445768',
    90: '#354452',
    100: '#232F3B',
  },
  grey: {
    5: '#FCFCFC',
    10: '#F7F7F7',
    20: '#F2F2F2',
    30: '#E9E9E9',
    40: '#C7C7C7',
    50: '#A9A9A9',
    60: '#808080',
    70: '#6B6B6B',
    80: '#4B4B4B',
    90: '#292929',
  },
  slate: {
    5: '#E9EAEE',
    10: '#C6CBD6',
    20: '#A3AABA',
    30: '#81899E',
    40: '#67708A',
    50: '#4E5879',
    60: '#475171',
    70: '#3F4766',
    80: '#383E58',
    90: '#2C2E3E',
  },
  plum: {
    5: '#EAE7EE',
    10: '#CAC3D5',
    20: '#A79BBA',
    30: '#86759E',
    40: '#6E598A',
    50: '#584078',
    60: '#523A72',
    70: '#483369',
    80: '#3F2D5F',
    90: '#322252',
  },
  fushcia: {
    5: '#F7E6ED',
    10: '#ECBFD4',
    20: '#E298B9',
    30: '#D9739D',
    40: '#D25B89',
    50: '#CD4A76',
    60: '#BC4670',
    70: '#A64168',
    80: '#903D61',
    90: '#6C3352',
  },
  crimson: {
    5: '#FEECEE',
    10: '#FED0D2',
    20: '#EDA09B',
    30: '#E27D75',
    40: '#EB6251',
    50: '#EE5738',
    60: '#E04E37',
    70: '#CE4531',
    80: '#C13F2B',
    90: '#B13721',
  },
  tangerine: {
    5: '#FFF3E0',
    10: '#FFE0B2',
    20: '#FFCC80',
    30: '#FFB74D',
    40: '#FFA726',
    50: '#FF9800',
    60: '#FB8C00',
    70: '#F57C00',
    80: '#EF6C00',
    90: '#E65000',
  },
  amber: {
    5: '#FFFEF3',
    10: '#FFFDE6',
    20: '#FFF9C1',
    30: '#FFF596',
    40: '#FFF06B',
    50: '#FEEB45',
    60: '#FCE613',
    70: '#FFD818',
    80: '#FFBF0D',
    90: '#FFA601',
  },
  cerulian: {
    5: '#E1F5FB',
    10: '#B3E4F4',
    20: '#82D2EE',
    30: '#54C0E6',
    40: '#33B3E3',
    50: '#14A6E0',
    60: '#0B98D2',
    70: '#0086BF',
    80: '#0075AB',
    90: '#00568A',
  },
  fern: {
    5: '#EBF4E9',
    10: '#D0E4C9',
    20: '#B3D3A8',
    30: '#96C287',
    40: '#82B66E',
    50: '#70A956',
    60: '#659B4E',
    70: '#598944',
    80: '#4E783B',
    90: '#3B592C',
  },

  gradient: {
    5: '#ce4531', // crimson-70-red
    10: '#de642d',
    20: '#e8792b',
    30: '#f28c29',
    40: '#f89928',
    50: '#ffa726', // tangerine-40-orange
    60: '#c4ae48',
    70: '#93b464',
    80: '#8bb569',
    90: '#82b66e', // fern-40-green
  },
  cerise: {
    5: '#eb3864',
  },
};

export const QUALITY_COLORS = {
  red: '#CE4532',
  green: '#82B66E',
  blue: '#0086BF',
  yellow: '#FFA727',
  purple: '#A5A8F0',
  lime: '#C3C801',
};

export const PRIMARY_COLORS = ['#7DB6EC', '#F8A35E', '#8085EA', '#F15C80', '#E5D455', '#29908F', '#F45B5A', '#93E9E1'];

export const DONUT_COLORS = [
  '#7DB6EC',
  '#90EF7E',
  '#8085EA',
  '#F15C80',
  '#E5D455',
  '#56B1B1',
  '#4F8AD9',
  '#46D23B',
  '#F8A35E',
  '#4844CC',
  '#D50E4A',
  '#268381',
  '#324A96',
  '#008319',
  '#2E169B',
  '#89003F',
  '#CF6B18',
  '#1D4743',
];

export const LV_COLOR_PALETTE: string[] = [
  '#3FAEFA',
  '#90EF7E',
  '#8085EA',
  '#F35349',
  '#6D8AB9',
  '#4EDECA',
  '#4F8AD9',
  '#42E079',
  '#FAB672',
  '#CF6DF6',
  '#D50E4A',
  '#268381',
  '#324A96',
  '#008319',
  '#5250C1',
  '#89003F',
  '#F86F40',
  '#1D4743',
];
/**
 * maps the 'normal' names to the fancy names
 */
export const COLORMAP = {
  primary: 'blueGrey',
  grey: 'grey',
  slate: 'slate',
  blue: 'cerulian',
  red: 'crimson',
  orange: 'tangerine',
  yellow: 'amber',
  green: 'fern',
  purple: 'plum',
  pink: 'fushcia',
  gradient: 'gradient',
  cerise: 'cerise',
};

export const LS_COLOR_PALETTE = {
  connectedColor: '#82B66E',
  disconnectedColor: '#C13F2B',
  processingColor: '#2890FF',
};

/**
 * These constants are for use OUTSIDE of SCSS.
 * When setting colours in SCSS, use the soti-color function from _soti-colors.scss
 */
export class ColorConstants {
  //White and Black
  static WHITE = (() => getStyleValue('--generic-white-color'))();
  static BLACK = (() => getStyleValue('--generic-black-color'))();

  static SOTI_BRAND_COLOR = (() => getStyleValue('--soti-brand-color'))();

  //A brighter shade of green for 'success' icons and messages
  static SUCCESS_GREEN = (() => getStyleValue('--messages-success-green-color'))();
  static ALERT_RED = (() => getStyleValue('--messages-alert-red-color'))();
  static DEFAULT_TEXT_COLOR = (() => getStyleValue('--messages-default-text-color'))();
  static LOCATION_UNSELECT = (() => getStyleValue('--messages-location-unselect-color'))();

  /**
   * An Array of all the secondary colours to be used in charts etc.
   */
  static CHART_COLORS = ColorConstants.colorGenerator(
    ['purple', 'yellow', 'slate', 'pink', 'blue'],
    [80, 50, 30],
    [ColorConstants.sotiColors('purple', 50), ColorConstants.sotiColors('yellow')]
  );

  static addTransparency(color: string, transparencyIndex: number = 0.2): string {
    const rgbColor: Color = Color(color);
    return `rgba( ${rgbColor.red()},${rgbColor.green()},${rgbColor.blue()},${transparencyIndex})`;
  }

  static badColourGenerator(shade) {
    const shadeNum = 255 - Math.max(0, Math.min(Math.round(shade * 2.55), 255));
    const shadeHex = ('00' + shadeNum.toString(16)).slice(-2);
    return `#FF${shadeHex}FF`;
  }

  static sotiColors(color: string, shade: number = 80): string {
    const colorMap = SOTICOLORS[ColorConstants.colorMapper(color)];
    if (!colorMap || !colorMap[shade]) {
      console.error(`Invalid Color Mapping ${color}:${shade}`);
      return this.badColourGenerator(shade);
    }
    return colorMap[shade];
  }

  static colorMapper(color: string): string {
    return COLORMAP[color];
  }

  static colorGenerator(
    acceptedColors: string[],
    acceptedShades: number[] = [80],
    exceptions: string[] = []
  ): string[] {
    const generatedColors: string[] = [];
    for (const shade of acceptedShades) {
      for (const color of acceptedColors) {
        const selectedColor = ColorConstants.sotiColors(color, shade);
        if (!exceptions.includes(selectedColor)) {
          generatedColors.push(selectedColor);
        }
      }
    }
    return generatedColors;
  }
}

export class TypographyConstants {
  static LABEL1: '13px';
  static LABEL2: '14px';
  static WEIGHT1: '400';
}

export const HighchartsConstant = {
  color: {
    grey40: (() => getStyleValue('--generic-grey40-color'))(),
    grey60: (() => getStyleValue('--generic-grey60-color'))(),
    inactiveArrow: (() => getStyleValue('--generic-inactive-arrow-color'))(),
    activeArrow: (() => getStyleValue('--generic-active-arrow-color'))(),
    labelColor: (() => getStyleValue('--generic-label-color'))(),
    gridLineColor: (() => getStyleValue('--generic-grid-line-color'))(),
    stackedBarChartsHoverBackground: (() => getStyleValue('--stacked-bar-charts-hover-bg-color'))(), //ColorConstants.addTransparency(ColorConstants.sotiColors('grey', 50)),
    legendHiddenColor: (() => getStyleValue('--generic-legend-hidden-color'))(),
  },
  defaultBucket: {
    good: (() => getStyleValue('--quality-color-green'))(), // ColorConstants.sotiColors('green', 40),
    bad: (() => getStyleValue('--quality-color-red'))(), // ColorConstants.sotiColors('red', 70),
    average: (() => getStyleValue('--quality-color-yellow'))(), // ColorConstants.sotiColors('orange', 40),
    nodata: (() => getStyleValue('--generic-no-data-color'))(),
  },
  dischargeBucket: {
    low: (() => getStyleValue('--discharge-bucket-low-color'))(), // ColorConstants.sotiColors('green', 40),
    veryHigh: (() => getStyleValue('--discharge-bucket-very-high-color'))(), // ColorConstants.sotiColors('red', 70),
    average: (() => getStyleValue('--discharge-bucket-average-color'))(), // ColorConstants.sotiColors('orange', 40),
    high: (() => getStyleValue('--discharge-bucket-high-color'))(), // ColorConstants.sotiColors('orange', 40),
  },
  batteryTemperatureBucket: {
    cold: (() => getStyleValue('--quality-color-blue'))(), // ColorConstants.sotiColors('blue', 70),
    hot: (() => getStyleValue('--quality-color-red'))(), // ColorConstants.sotiColors('red', 70),
    both: (() => getStyleValue('--quality-color-purple'))(), //Large fluctuations color
    normal: (() => getStyleValue('--quality-color-green'))(), // ColorConstants.sotiColors('green', 40),
  },
  chargeTypeBucket: {
    usb: (() => getStyleValue('--charge-type-bucket-usb-color'))(),
    ac: (() => getStyleValue('--charge-type-bucket-ac-color'))(),
    wireless: (() => getStyleValue('--charge-type-bucket-wireless-color'))(),
    dock: (() => getStyleValue('--charge-type-bucket-dock-color'))(),
  },
  dataUsageTypeBucket: {
    total: (() => getStyleValue('--data-usage-static-bucket-item-2'))(),
    wifi: (() => getStyleValue('--location-static-bucket-item-5'))(),
    ether: (() => getStyleValue('--data-usage-static-bucket-item-4'))(),
    cellular: (() => getStyleValue('--location-static-bucket-item-4'))(),
    roaming: (() => getStyleValue('--location-static-bucket-item-1'))(),
  },
  chargingPatternBucket: {
    random: (() => getStyleValue('--charge-pattern-randomly-charged'))(),
    spot: (() => getStyleValue('--charge-pattern-spot-charged'))(), // ColorConstants.sotiColors('red', 70),
    optimal: (() => getStyleValue('--charge-pattern-optimally-charged'))(), // ColorConstants.sotiColors('orange', 40),
    overCharged: (() => getStyleValue('--charge-pattern-overcharged'))(),
  },
  batteryStaticBucket: {
    item1: (() => getStyleValue('--battery-static-bucket-item-1'))(),
    item2: (() => getStyleValue('--battery-static-bucket-item-2'))(),
    item3: (() => getStyleValue('--battery-static-bucket-item-3'))(),
    item4: (() => getStyleValue('--battery-static-bucket-item-4'))(),
    item5: (() => getStyleValue('--battery-static-bucket-item-5'))(),
    other: (() => getStyleValue('--battery-static-bucket-item-other'))(),
  },
  appUsageStaticBucket: {
    item1: (() => getStyleValue('--app-usage-static-bucket-item-1'))(),
    item2: (() => getStyleValue('--app-usage-static-bucket-item-2'))(),
    item3: (() => getStyleValue('--app-usage-static-bucket-item-3'))(),
    item4: (() => getStyleValue('--app-usage-static-bucket-item-4'))(),
    item5: (() => getStyleValue('--app-usage-static-bucket-item-5'))(),
    other: (() => getStyleValue('--app-usage-static-bucket-item-other'))(),
  },
  dataUsageStaticBucket: {
    item1: (() => getStyleValue('--data-usage-static-bucket-item-1'))(),
    item2: (() => getStyleValue('--data-usage-static-bucket-item-2'))(),
    item3: (() => getStyleValue('--data-usage-static-bucket-item-3'))(),
    item4: (() => getStyleValue('--data-usage-static-bucket-item-4'))(),
    item5: (() => getStyleValue('--data-usage-static-bucket-item-5'))(),
    other: (() => getStyleValue('--data-usage-static-bucket-item-other'))(),
  },
  locationStaticBucket: {
    item1: (() => getStyleValue('--location-static-bucket-item-1'))(),
    item2: (() => getStyleValue('--location-static-bucket-item-2'))(),
    item3: (() => getStyleValue('--location-static-bucket-item-3'))(),
    item4: (() => getStyleValue('--location-static-bucket-item-4'))(),
    item5: (() => getStyleValue('--location-static-bucket-item-5'))(),
    other: (() => getStyleValue('--location-static-bucket-item-other'))(),
  },
  chargeCyclesBucket: {
    good: (() => getStyleValue('--quality-color-green'))(),
    average: (() => getStyleValue('--quality-color-yellow'))(),
    poor: (() => getStyleValue('--quality-color-blue'))(),
    bad: (() => getStyleValue('--quality-color-red'))(),
    nodata: (() => getStyleValue('--generic-no-data-color'))(),
  },
  batteryStatusBucket: {
    New: (() => getStyleValue('--quality-color-green'))(),
    Active: (() => getStyleValue('--battery-status-bucket-active-color'))(),
    Inactive: (() => getStyleValue('--battery-status-bucket-inactive-color'))(),
    Decommissioned: (() => getStyleValue('--battery-status-bucket-decommissioned-color'))(),
    colorActive: (() => getStyleValue('--battery-status-bucket-colorActive-color'))(),
    colorInactive: (() => getStyleValue('--battery-status-bucket-colorInactive-color'))(),
  },

  signalStrengthBucket: {
    good: (() => getStyleValue('--quality-color-green'))(), // ColorConstants.sotiColors('green', 40),
    bad: (() => getStyleValue('--quality-color-red'))(), // ColorConstants.sotiColors('red', 70),
    average: (() => getStyleValue('--quality-color-yellow'))(), // ColorConstants.sotiColors('orange', 40),
    nodata: (() => getStyleValue('--generic-no-data-color'))(),
    nosignal: (() => getStyleValue('--generic-grey60-color'))(),
  },
  fonts: { labels: TypographyConstants.LABEL1, label2: TypographyConstants.LABEL2 },
  fontWeights: { weight1: TypographyConstants.WEIGHT1 },
  colorPalettes: {
    barCharts: (() => getStyleValue('--bar-chart-colors').replace(/\s/g, '').split(','))(),
    stackedBarCharts: (() => getStyleValue('--stacked-bar-chart-colors').replace(/\s/g, '').split(','))(),
    lineChart: (() => getStyleValue('--line-chart-colors').replace(/\s/g, '').split(','))(),
    donutCharts: (() => getStyleValue('--donut-chart-colors').replace(/\s/g, '').split(','))(),
    defaultPalette: (() => getStyleValue('--bar-chart-colors').replace(/\s/g, '').split(','))(),
    stackedDonutActiveShades: (() =>
      getStyleValue('--active-stacked-donut-chart-colors').replace(/\s/g, '').split(','))(),
    stackedDonutInactiveShades: (() =>
      getStyleValue('--inactive-stacked-donut-chart-colors').replace(/\s/g, '').split(','))(),
    stackedDonutNewShades: (() => getStyleValue('--new-stacked-donut-chart-colors').replace(/\s/g, '').split(','))(),
    stackedDonutDecommissionedShades: (() =>
      getStyleValue('--decomm-stacked-donut-chart-colors').replace(/\s/g, '').split(','))(),
  },
  UtilizationBucket: {
    Utilized: (() => getStyleValue('--store-utilization-utilized-color'))(),
    NotUtilized: (() => getStyleValue('--store-utilization-not-utilized-color'))(),
    Offline: (() => getStyleValue('--store-utilization-offline-color'))(),
    Average: (() => getStyleValue('--store-utilization-average-color'))(),
    NoData: (() => getStyleValue('--store-utilization-no-data-color'))(),
  },
  webAppError: {
    ClientError: (() => getStyleValue('--webApp-error-client-color'))(),
    ServerError: (() => getStyleValue('--webApp-error-server-color'))(),
    SSLError: (() => getStyleValue('--webApp-error-ssl-color'))(),
    WebViewError: (() => getStyleValue('--webApp-error-webview-color'))(),
  }
};

/**
 * Takes a palette and randomly shifts the starting element. Result can be reversed
 * @param colorPalette Palette to be shifted
 * @param reverseable whether reversing the palette is allowed
 * @returns shifted palette
 */
export function paletteShifter(colorPalette: string[], reverseable: boolean = false) {
  const reverse = reverseable ? Math.random() < 0.5 : false;
  const len = colorPalette.length;
  const start = Math.floor(Math.random() * len);
  const result: string[] = [];

  let i: number;
  for (i = start; i < len; i++) {
    result.push(colorPalette[i]);
  }
  for (i = 0; i < start; i++) {
    result.push(colorPalette[i]);
  }

  if (reverse) {
    return result.reverse();
  }

  return result;
}

function getStyleValue(variableName: string) {
  const bodyStyles = window.getComputedStyle(document.body);
  const value = bodyStyles.getPropertyValue(variableName);
  if (value === '') {
    console.warn('No match for style: ' + variableName);
  }
  return value;
}
